import type { ApiResponse } from '../../types'
import type {
  RoomCardDto,
  RoomDetailDto,
  ChatMessageDto,
  CreateRoomRequest,
  PostPresenceRequest,
  PostMessageRequest,
  PostMessageResult,
} from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_Rooms — monitored lobby rooms, presence and server-filtered chat.
// The acting person is SERVER-HELD (COTC_GroupsModelRevision R11).
// Presence may assert an expected profile only to reject stale requests after switching.
// Read failures surface `errors[0] ?? message`.

/** POST /rooms — open a monitored lobby (eligible hosts only). */
export async function createRoom(req: CreateRoomRequest): Promise<RoomDetailDto> {
  try {
    const res = await client.post<ApiResponse<RoomDetailDto>>('/rooms', req)
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /rooms/open — rooms open across the acting person's circles. */
export async function getOpenRooms(): Promise<RoomCardDto[]> {
  try {
    const res = await client.get<ApiResponse<RoomCardDto[]>>('/rooms/open')
    return unwrap(res.data) ?? []
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /rooms/{id} — full room detail (member-only; opaque 404 otherwise). */
export async function getRoom(id: string): Promise<RoomDetailDto> {
  try {
    const res = await client.get<ApiResponse<RoomDetailDto>>(`/rooms/${id}`)
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /rooms/{id}/messages?after= — incremental chat poll. */
export async function getRoomMessages(
  id: string,
  after?: string,
): Promise<ChatMessageDto[]> {
  try {
    const params: Record<string, string> = {}
    if (after) params.after = after
    const res = await client.get<ApiResponse<ChatMessageDto[]>>(`/rooms/${id}/messages`, { params })
    return unwrap(res.data) ?? []
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** POST /rooms/{id}/presence — heartbeat upsert. */
export async function postPresence(id: string, req: PostPresenceRequest): Promise<void> {
  try {
    await client.post<ApiResponse>(`/rooms/${id}/presence`, req)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/**
 * POST /rooms/{id}/messages — send (server-side content-hold filter applies).
 * A held message returns `{ held: true }` (HTTP 202) with no message body — the
 * author never sees it (COTC_Rooms R3).
 */
export async function postMessage(id: string, req: PostMessageRequest): Promise<PostMessageResult> {
  try {
    const res = await client.post<ApiResponse<PostMessageResult>>(`/rooms/${id}/messages`, req)
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** POST /rooms/{id}/close — host, host's guardian, or moderator. */
export async function closeRoom(id: string): Promise<void> {
  try {
    await client.post<ApiResponse>(`/rooms/${id}/close`, {})
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}
